import { CenterItem, Column, RowAlignedItems } from '@components/LinearLayout';
import {
  CheckboxText,
  FormItemContainer,
  FormItemWrapper,
  FormItemsWrapper,
  HintText,
  RegistrationFormRoot,
} from '../RegistrationForm/styles';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { Dispatch, SetStateAction } from 'react';
import {
  InputFieldsType,
  SelectOptionsType,
  getValidationRules,
} from '@composites/InboundButton/utils';
import { Trans, useTranslation } from 'react-i18next';

import Checkbox from '@components/Checkbox';
import { ContactApproval } from '@pages/EventContactPage/components/ContactForm/styles';
import FormItem from '@composites/FormItem';
import { FormItemLabel } from '@composites/FormItem/styles';
import { TextLink } from '@composites/EventsSection/styles';
import WeDropDown from '@composites/WeDropdown';
import WeInput from '@composites/WeInput';
import WePhoneInput from '@components/WePhoneInput';
import { getEventDetails } from '@store/slices/eventSlice';
import { useSelector } from 'react-redux';
import { useTabletWidth } from '@utils/hooks';
import { useTheme } from 'styled-components';

interface RegistrationMultiFormProps {
  control: Control<Record<string, string>, any>;
  inputElements: InputFieldsType;
  errors: FieldErrors<Record<string, string>>;
  setCheckedApproval: Dispatch<SetStateAction<boolean>>;
  checkedApproval: boolean;
  setFormsNumber: Dispatch<SetStateAction<number>>;
  formsNumber: number;
  setValue: UseFormSetValue<Record<string, string>>;
  watch: UseFormWatch<Record<string, string>>;
}
const allowedFormsNumber = [1, 2, 3, 4, 5];

const RegistrationMultiForm = ({
  control,
  errors,
  inputElements,
  checkedApproval,
  setCheckedApproval,
  formsNumber,
  setFormsNumber,
  setValue,
  watch,
}: RegistrationMultiFormProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useTabletWidth();
  const sluggedEvent = useSelector(getEventDetails)?.[0];

  return (
    <RegistrationFormRoot>
      <FormItemsWrapper>
        {inputElements.map(item => {
          const width = isMobile
            ? `${(item.mobile / 12) * 100}%`
            : `${(item.desktop / 12) * 100}%`;

          const rules = getValidationRules(item);

          const selectOptions = item?.options?.map(item => ({
            value: item.value,
            label: t(item.label),
          }));

          const formChild = () => {
            switch (item.type) {
              case 'input-select':
                return (
                  <FormItem
                    gap="0.2rem"
                    label={`${t(item.id)}:`}
                    labelColor={theme.color.mobilityGrey}
                    width="100%"
                    required={item.required}
                    paddingLabel="1rem"
                    error={errors[item.id]?.message}
                  >
                    <Controller
                      control={control}
                      name={item.id}
                      rules={rules}
                      render={({ field }) => (
                        <WeDropDown
                          options={selectOptions as SelectOptionsType}
                          placeholder={t('select')}
                          border={`0.4rem solid ${theme.color.strokeGrey}`}
                          borderRadius={theme.borderRadius.radius5}
                          fontSize={theme.fontSize.size18}
                          padding="1.5rem 2rem"
                          color={theme.color.mobilityGrey}
                          hoverColor={theme.color.mobilityGrey}
                          minWidth="100%"
                          maxWidth="100%"
                          {...field}
                        />
                      )}
                    />
                  </FormItem>
                );

              case 'input-text':
                return (
                  <FormItem
                    gap="0.2rem"
                    label={`${t(item.id)}:`}
                    labelColor={theme.color.mobilityGrey}
                    width="100%"
                    required={item.required}
                    paddingLabel="1rem"
                    error={errors[item.id]?.message}
                  >
                    <Controller
                      control={control}
                      name={item.id}
                      rules={rules}
                      render={({ field }) => (
                        <WeInput
                          placeholder={t(item.id)}
                          border={`0.4rem solid ${theme.color.strokeGrey}`}
                          focusBorder={`0.4rem solid ${sluggedEvent?.design?.themeColor}`}
                          borderRadius={theme.borderRadius.radius5}
                          fontSize={theme.fontSize.size18}
                          padding="1.5rem 2rem"
                          color={theme.color.mobilityGrey}
                          focusColor={theme.color.mobilityGrey}
                          backgroundColor="white"
                          minWidth="100%"
                          maxWidth="100%"
                          height="5.7rem"
                          {...field}
                        />
                      )}
                    />
                  </FormItem>
                );
              case 'phone-number':
                return (
                  <FormItem
                    gap="0.2rem"
                    label={`${t(item.id)}:`}
                    labelColor={theme.color.mobilityGrey}
                    width="100%"
                    required={item.required}
                    paddingLabel="1rem"
                    error={errors[item.id]?.message}
                  >
                    <Controller
                      control={control}
                      name={item.id}
                      rules={rules}
                      render={({ field }) => <WePhoneInput {...field} />}
                    />
                  </FormItem>
                );

              default:
                return null;
            }
          };

          return (
            <FormItemWrapper key={item.id} $width={width}>
              {formChild()}
            </FormItemWrapper>
          );
        })}

        <HintText>*{t('required-fields')}</HintText>
      </FormItemsWrapper>
      <FormItemContainer>
        <FormItemLabel
          $paddingLabel="0 0 0 1rem"
          $labelColor={theme.color.mobilityGrey}
        >
          {t('would-you-like-to-attend-the-following-evening-events')}
        </FormItemLabel>
        <Column $gap="3rem">
          <Checkbox
            label={
              <RowAlignedItems>
                <CheckboxText>{t('icebreaker')}</CheckboxText>
              </RowAlignedItems>
            }
            spacing={theme.spacing.size3}
            checked={watch('icebreaker') === 'true'}
            type="square"
            size="2.5rem"
            backgroundColor={theme.color.white}
            color={theme.color.mobilityGrey}
            borderRadius={theme.borderRadius.radius5}
            alignItems="start"
            border={`0.4rem solid ${theme.color.strokeGrey}`}
            onChange={() => {
              setValue(
                'icebreaker',
                watch('icebreaker') === 'true' ? 'false' : 'true'
              );
            }}
          />
          <Checkbox
            label={
              <RowAlignedItems>
                <CheckboxText>{t('dinner')}</CheckboxText>
              </RowAlignedItems>
            }
            spacing={theme.spacing.size3}
            checked={watch('dinner') === 'true'}
            type="square"
            size="2.5rem"
            backgroundColor={theme.color.white}
            color={theme.color.mobilityGrey}
            borderRadius={theme.borderRadius.radius5}
            alignItems="start"
            border={`0.4rem solid ${theme.color.strokeGrey}`}
            onChange={() => {
              setValue('dinner', watch('dinner') === 'true' ? 'false' : 'true');
            }}
          />
        </Column>
      </FormItemContainer>
      {new Array(formsNumber).fill(0)?.map((element, index) => (
        <Column key={index}>
          <FormItemsWrapper>
            {inputElements.map(item => {
              const width = isMobile
                ? `${(item.mobile / 12) * 100}%`
                : `${(item.desktop / 12) * 100}%`;

              const rules = getValidationRules(item);

              const selectOptions = item?.options?.map(item => ({
                value: item.value,
                label: t(item.label),
              }));

              const formChild = () => {
                switch (item.type) {
                  case 'input-select':
                    return (
                      <FormItem
                        gap="0.2rem"
                        label={`${t(item.id)}:`}
                        labelColor={theme.color.mobilityGrey}
                        width="100%"
                        required={item.required}
                        paddingLabel="1rem"
                        error={errors[item.id]?.message}
                      >
                        <Controller
                          control={control}
                          name={`${item.id}-${index + 1}`}
                          rules={rules}
                          render={({ field }) => (
                            <WeDropDown
                              options={selectOptions as SelectOptionsType}
                              placeholder={t('select')}
                              border={`0.4rem solid ${theme.color.strokeGrey}`}
                              borderRadius={theme.borderRadius.radius5}
                              fontSize={theme.fontSize.size18}
                              padding="1.5rem 2rem"
                              color={theme.color.mobilityGrey}
                              hoverColor={theme.color.mobilityGrey}
                              minWidth="100%"
                              maxWidth="100%"
                              {...field}
                            />
                          )}
                        />
                      </FormItem>
                    );

                  case 'input-text':
                    return (
                      <FormItem
                        gap="0.2rem"
                        label={`${t(item.id)}:`}
                        labelColor={theme.color.mobilityGrey}
                        width="100%"
                        required={item.required}
                        paddingLabel="1rem"
                        error={errors[item.id]?.message}
                      >
                        <Controller
                          control={control}
                          name={`${item.id}-${index + 1}`}
                          rules={rules}
                          render={({ field }) => (
                            <WeInput
                              placeholder={t(item.id)}
                              border={`0.4rem solid ${theme.color.strokeGrey}`}
                              focusBorder={`0.4rem solid ${sluggedEvent?.design?.themeColor}`}
                              borderRadius={theme.borderRadius.radius5}
                              fontSize={theme.fontSize.size18}
                              padding="1.5rem 2rem"
                              color={theme.color.mobilityGrey}
                              focusColor={theme.color.mobilityGrey}
                              backgroundColor="white"
                              minWidth="100%"
                              maxWidth="100%"
                              height="5.7rem"
                              {...field}
                            />
                          )}
                        />
                      </FormItem>
                    );
                  case 'phone-number':
                    return (
                      <FormItem
                        gap="0.2rem"
                        label={`${t(item.id)}:`}
                        labelColor={theme.color.mobilityGrey}
                        width="100%"
                        required={item.required}
                        paddingLabel="1rem"
                        error={errors[item.id]?.message}
                      >
                        <Controller
                          control={control}
                          name={`${item.id}-${index + 1}`}
                          rules={rules}
                          render={({ field }) => <WePhoneInput {...field} />}
                        />
                      </FormItem>
                    );

                  default:
                    return null;
                }
              };

              return (
                <FormItemWrapper key={item.id} $width={width}>
                  {formChild()}
                </FormItemWrapper>
              );
            })}

            <HintText>*{t('required-fields')}</HintText>
          </FormItemsWrapper>
          <FormItemContainer>
            <FormItemLabel
              $paddingLabel="0 0 0 1rem"
              $labelColor={theme.color.mobilityGrey}
            >
              {t('would-you-like-to-attend-the-following-evening-events')}
            </FormItemLabel>

            <Column $gap="3rem">
              <Checkbox
                label={
                  <RowAlignedItems>
                    <CheckboxText>{t('icebreaker')}</CheckboxText>
                  </RowAlignedItems>
                }
                spacing={theme.spacing.size3}
                checked={watch(`icebreaker-${index + 1}`) === 'true'}
                type="square"
                size="2.5rem"
                backgroundColor={theme.color.white}
                color={theme.color.mobilityGrey}
                borderRadius={theme.borderRadius.radius5}
                alignItems="start"
                border={`0.4rem solid ${theme.color.strokeGrey}`}
                onChange={() => {
                  setValue(
                    `icebreaker-${index + 1}`,
                    watch(`icebreaker-${index + 1}`) === 'true'
                      ? 'false'
                      : 'true'
                  );
                }}
              />
              <Checkbox
                label={
                  <RowAlignedItems>
                    <CheckboxText>{t('dinner')}</CheckboxText>
                  </RowAlignedItems>
                }
                spacing={theme.spacing.size3}
                checked={watch(`dinner-${index + 1}`) === 'true'}
                type="square"
                size="2.5rem"
                backgroundColor={theme.color.white}
                color={theme.color.mobilityGrey}
                borderRadius={theme.borderRadius.radius5}
                alignItems="start"
                border={`0.4rem solid ${theme.color.strokeGrey}`}
                onChange={() => {
                  setValue(
                    `dinner-${index + 1}`,
                    watch(`dinner-${index + 1}`) === 'true' ? 'false' : 'true'
                  );
                }}
              />
            </Column>
          </FormItemContainer>
        </Column>
      ))}

      <FormItemContainer>
        <FormItemLabel
          $paddingLabel="0 0 0 1rem"
          $labelColor={theme.color.mobilityGrey}
        >
          {t('do-you-want-to-add-more-registrations')}
        </FormItemLabel>
        <Column $gap="1rem">
          <RowAlignedItems $gap="4rem">
            {allowedFormsNumber.map(element => (
              <Checkbox
                key={element}
                label={
                  <CenterItem>
                    <CheckboxText>{element}</CheckboxText>
                  </CenterItem>
                }
                spacing={theme.spacing.size3}
                checked={formsNumber === element}
                type="round"
                size="2.5rem"
                backgroundColor={theme.color.mobilityGrey}
                color={theme.color.mobilityGrey}
                alignItems="start"
                border={`0.4rem solid ${theme.color.strokeGrey}`}
                onChange={() => {
                  if (formsNumber !== element) setFormsNumber(element);
                }}
              />
            ))}
          </RowAlignedItems>
          <Checkbox
            label={
              <CenterItem>
                <CheckboxText>{t('no-more-forms')}</CheckboxText>
              </CenterItem>
            }
            spacing={theme.spacing.size3}
            checked={formsNumber === 0}
            type="round"
            size="2.5rem"
            backgroundColor={theme.color.mobilityGrey}
            color={theme.color.mobilityGrey}
            alignItems="start"
            border={`0.4rem solid ${theme.color.strokeGrey}`}
            onChange={() => {
              if (formsNumber !== 0) setFormsNumber(0);
            }}
          />
        </Column>
      </FormItemContainer>
      <ContactApproval>
        <Checkbox
          label={
            <RowAlignedItems>
              <CheckboxText $required>
                {t('inbound-text-approval-1')}
                <Trans i18nKey="inbound-text-approval-2">
                  Further information can be found in our
                  <TextLink
                    href="https://we-conect.com/data-privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    privacy policy
                  </TextLink>
                  .
                </Trans>
              </CheckboxText>
            </RowAlignedItems>
          }
          spacing={theme.spacing.size10}
          checked={checkedApproval}
          type="square"
          size="3.2rem"
          backgroundColor={theme.color.white}
          color={theme.color.mobilityGrey}
          borderRadius={theme.borderRadius.radius5}
          alignItems="start"
          border={`0.4rem solid ${theme.color.strokeGrey}`}
          onChange={() => {
            setCheckedApproval(prev => !prev);
          }}
        />
      </ContactApproval>
    </RegistrationFormRoot>
  );
};

export default RegistrationMultiForm;
